import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import MyLinksSettings from './components/MyLinksSettings';
import { getStoreCapabilities } from '@/lib/accountType';
import { buildSubdomain } from '@/lib/domain';
import {
  loadPays,
  loadVilles,
  resolvePaysName,
  resolveVilleName,
  villesForPays,
  type PaysOption,
  type VilleOption,
} from '@/lib/geo';
import StorePreview from '@/pages/dashboard/store/components/StorePreview';

interface AccountTypeOption {
  id: number;
  label: string;
  desc: string;
  icon: string;
  color: string;
}

const ACCOUNT_TYPES: AccountTypeOption[] = [
  { id: 3, label: 'Boutique', desc: 'Produits uniquement', icon: 'ri-shopping-bag-3-line', color: 'bg-primary-500' },
  { id: 4, label: 'Services', desc: 'Services uniquement', icon: 'ri-service-line', color: 'bg-accent-500' },
  { id: 7, label: 'Mixte', desc: 'Produits + Services', icon: 'ri-apps-2-line', color: 'bg-secondary-500' },
];

const PRESET_COLORS = [
  { hex: '#155C9C', name: 'Bleu' },
  { hex: '#2D5A27', name: 'Forêt' },
  { hex: '#8B1E3F', name: 'Bordeaux' },
  { hex: '#B36023', name: 'Terracotta' },
  { hex: '#3D6B6B', name: 'Teal' },
  { hex: '#5C3D8F', name: 'Violet' },
  { hex: '#C44569', name: 'Rose' },
  { hex: '#3A3A3A', name: 'Charcoal' },
];

export default function StorePage() {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [paysList, setPaysList] = useState<PaysOption[]>([]);
  const [villesList, setVillesList] = useState<VilleOption[]>([]);
  const [logoUploading, setLogoUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [mylinksCopied, setMylinksCopied] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    nomcommerce: user?.nomcommerce || '',
    description: user?.description || '',
    aboutus: user?.aboutus || '',
    telephone: user?.telephone || '',
    adresse: user?.adresse || '',
    pays: user?.['Pays'] || '',
    ville: user?.['Ville'] || '',
    monaie: user?.monaie || 'MAD',
    langue: user?.langue || 'fr',
    typecompte: user?.typecompte || 3,
    type: user?.type || 'Boutique',
    image: user?.image || '',
    imageaboutus: user?.imageaboutus || '',
    couleurcharte: user?.couleurcharte || '#155C9C',
    tva: user?.tva || '',
  });

  // Charge le référentiel pays/villes puis résout les éventuels codes stockés
  // (« 212 », « 1 ») en noms lisibles (« Maroc », « Fès »).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [pays, villes] = await Promise.all([loadPays(), loadVilles()]);
      if (cancelled) return;
      setPaysList(pays);
      setVillesList(villes);
      setForm((prev) => ({
        ...prev,
        pays: resolvePaysName(prev.pays, pays),
        ville: resolveVilleName(prev.ville, villes),
      }));
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (field: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
    setSaveError('');
  };

  const handleSelectType = (opt: AccountTypeOption) => {
    setForm((prev) => ({ ...prev, typecompte: opt.id, type: opt.label }));
    setSaved(false);
  };

  const handleUpload = async (file: File, field: 'image' | 'imageaboutus') => {
    const setUploading = field === 'image' ? setLogoUploading : setImageUploading;
    setUploadMsg(null);
    setUploading(true);
    try {
      const url = await uploadMediaFile(file, 'branding');
      if (url) handleChange(field, url);
    } catch (err: unknown) {
      setUploadMsg(err instanceof Error ? err.message : "Erreur lors de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          nomcommerce: form.nomcommerce,
          description: form.description,
          aboutus: form.aboutus,
          telephone: form.telephone,
          adresse: form.adresse,
          Pays: form.pays,
          Ville: form.ville,
          monaie: form.monaie,
          langue: form.langue,
          typecompte: form.typecompte,
          type: form.type,
          image: form.image,
          imageaboutus: form.imageaboutus,
          couleurcharte: form.couleurcharte,
          tva: form.tva,
        })
        .eq('id', user.id);

      if (error) throw error;
      updateUser({
        nomcommerce: form.nomcommerce,
        description: form.description,
        aboutus: form.aboutus,
        telephone: form.telephone,
        adresse: form.adresse,
        'Pays': form.pays,
        'Ville': form.ville,
        monaie: form.monaie,
        langue: form.langue,
        typecompte: form.typecompte,
        type: form.type,
        image: form.image,
        imageaboutus: form.imageaboutus,
        couleurcharte: form.couleurcharte,
        tva: form.tva,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Impossible d'enregistrer vos modifications. Réessayez.",
      );
    } finally {
      setSaving(false);
    }
  };

  const caps = getStoreCapabilities(form.type);
  const villeOptions = villesForPays(form.pays, paysList, villesList);
  const subdomain = user?.user_name ? buildSubdomain(user.user_name).replace(/^https?:\/\//, '') : '';
  const myLinksUrl = subdomain ? `https://${subdomain}/mylinks` : '';

  const copyMyLinks = async () => {
    if (!myLinksUrl) return;
    try {
      await navigator.clipboard.writeText(myLinksUrl);
      setMylinksCopied(true);
      window.setTimeout(() => setMylinksCopied(false), 2200);
    } catch {
      /* presse-papiers indisponible */
    }
  };
  const menuPreview = [
    { label: 'Accueil', icon: 'ri-home-line' },
    ...(caps.products ? [{ label: 'Nos produits', icon: 'ri-shopping-bag-line' }] : []),
    ...(caps.services ? [{ label: 'Nos services', icon: 'ri-service-line' }] : []),
  ];

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Ma boutique</h2>
        <p className="text-sm text-foreground-500 mt-1">Personnalisez les informations et l&apos;identité de votre boutique</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6 items-start">
        <div className="max-w-2xl lg:max-w-none">
        {/* ── Identité visuelle ── */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-4">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-palette-line text-primary-500"></i>
            Identité visuelle
          </h3>

          <div className="flex flex-col sm:flex-row gap-5">
            {/* Logo */}
            <div className="flex-1">
              <label className="block text-xs font-medium text-foreground-600 mb-2">Logo de la boutique</label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-100 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                  {form.image ? (
                    <img src={form.image} alt="Logo" className="w-full h-full object-contain p-1" />
                  ) : (
                    <i className="ri-store-2-line text-2xl text-foreground-300"></i>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => logoRef.current?.click()}
                    disabled={logoUploading}
                    className="flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {logoUploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                    {form.image ? 'Changer le logo' : 'Ajouter un logo'}
                  </button>
                  {form.image && (
                    <button
                      type="button"
                      onClick={() => handleChange('image', '')}
                      className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>Retirer
                    </button>
                  )}
                </div>
                <input
                  ref={logoRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file, 'image');
                    if (logoRef.current) logoRef.current.value = '';
                  }}
                />
              </div>
            </div>

            {/* Image d'identité */}
            <div className="flex-1">
              <label className="block text-xs font-medium text-foreground-600 mb-2">Image d&apos;identité (bannière)</label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-100 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                  {form.imageaboutus ? (
                    <img src={form.imageaboutus} alt="Identité" className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-image-line text-2xl text-foreground-300"></i>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => imageRef.current?.click()}
                    disabled={imageUploading}
                    className="flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {imageUploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                    {form.imageaboutus ? 'Changer' : 'Ajouter une image'}
                  </button>
                  {form.imageaboutus && (
                    <button
                      type="button"
                      onClick={() => handleChange('imageaboutus', '')}
                      className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>Retirer
                    </button>
                  )}
                </div>
                <input
                  ref={imageRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(file, 'imageaboutus');
                    if (imageRef.current) imageRef.current.value = '';
                  }}
                />
              </div>
            </div>
          </div>

          {uploadMsg && (
            <p className="text-xs text-red-500 mt-3 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>{uploadMsg}
            </p>
          )}

          {/* Couleur principale */}
          <div className="mt-5 pt-5 border-t border-background-200/70">
            <label className="block text-xs font-medium text-foreground-600 mb-2">Couleur principale</label>
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 flex-shrink-0">
                <input
                  type="color"
                  value={form.couleurcharte}
                  onChange={(e) => handleChange('couleurcharte', e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div
                  className="w-10 h-10 rounded-lg border border-background-300/60 cursor-pointer"
                  style={{ backgroundColor: form.couleurcharte }}
                ></div>
              </div>
              <input
                type="text"
                value={form.couleurcharte}
                onChange={(e) => handleChange('couleurcharte', e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`)}
                maxLength={7}
                className="w-32 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm font-mono text-foreground-900 focus:outline-none focus:border-primary-300"
              />
              <div className="flex items-center gap-1.5 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => handleChange('couleurcharte', c.hex)}
                    title={c.name}
                    className={`w-7 h-7 rounded-md border-2 cursor-pointer transition-transform hover:scale-110 ${
                      form.couleurcharte.toUpperCase() === c.hex.toUpperCase() ? 'border-foreground-900' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: c.hex }}
                  ></button>
                ))}
              </div>
            </div>
            <p className="text-xs text-foreground-400 mt-2">
              Cette couleur s&apos;applique à tout votre site public (boutons, liens, badges).
            </p>
          </div>
        </div>

        {/* ── Type de compte ── */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
              <i className="ri-apps-2-line text-accent-500"></i>
              Type de compte
            </h3>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold text-background-50 ${ACCOUNT_TYPES.find((t) => t.id === form.typecompte)?.color || 'bg-primary-500'}`}>
              {form.type || 'Boutique'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {ACCOUNT_TYPES.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSelectType(opt)}
                className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${
                  form.typecompte === opt.id
                    ? 'border-primary-400 bg-primary-50/50'
                    : 'border-background-200/70 hover:border-background-300/60'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className={`${opt.icon} text-base ${form.typecompte === opt.id ? 'text-primary-600' : 'text-foreground-400'}`}></i>
                  <span className="text-sm font-semibold text-foreground-900">{opt.label}</span>
                </div>
                <span className="text-xs text-foreground-500">{opt.desc}</span>
              </button>
            ))}
          </div>

          {/* Aperçu du menu public */}
          <div className="bg-background-100 rounded-lg p-4">
            <span className="text-xs font-medium text-foreground-500 flex items-center gap-1.5 mb-3">
              <i className="ri-eye-line"></i>Aperçu du menu de votre site public
            </span>
            <div className="flex items-center gap-2 flex-wrap bg-background-50 border border-background-200/70 rounded-lg px-3 py-2">
              <span className="flex items-center gap-1.5 text-xs font-bold text-foreground-950 mr-2">
                {form.image ? (
                  <span className="w-5 h-5 rounded overflow-hidden inline-block">
                    <img src={form.image} alt="" className="w-full h-full object-contain" />
                  </span>
                ) : null}
                {form.nomcommerce || 'Ma boutique'}
              </span>
              {menuPreview.map((m) => (
                <span key={m.label} className="flex items-center gap-1 text-xs text-foreground-600 px-2 py-1 rounded-full bg-background-100">
                  <i className={`${m.icon} text-xs`}></i>
                  {m.label}
                </span>
              ))}
            </div>
            <p className="text-xs text-foreground-400 mt-2">
              {caps.products && caps.services
                ? 'Votre site public affiche à la fois vos produits et vos services.'
                : caps.products
                  ? 'Votre site public n\'affiche que vos produits (pas de section services).'
                  : 'Votre site public n\'affiche que vos services (pas de section produits).'}
            </p>
          </div>
        </div>

        {/* ── Informations légales & fiscalité ── */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-4">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-file-list-3-line text-secondary-700"></i>
            Informations légales &amp; fiscalité
          </h3>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">
              Numéro d&apos;inscription à la taxe professionnelle (TVA)
            </label>
            <input
              type="text"
              value={form.tva}
              onChange={(e) => handleChange('tva', e.target.value)}
              placeholder="Ex : 12345678"
              className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
            />
            <div className={`mt-2 flex items-start gap-2 text-xs rounded-lg px-3 py-2 ${
              form.tva.trim() ? 'bg-accent-50 text-accent-700' : 'bg-background-100 text-foreground-500'
            }`}>
              <i className={form.tva.trim() ? 'ri-checkbox-circle-line mt-0.5' : 'ri-information-line mt-0.5'}></i>
              <span>
                {form.tva.trim()
                  ? 'Le numéro est renseigné : la TVA sera affichée et calculée sur votre caisse, votre panier et la page de commande.'
                  : "Tant qu'aucun numéro n'est renseigné, la TVA n'est pas appliquée — aucun montant de TVA ne s'affiche sur votre site ni dans votre caisse."}
              </span>
            </div>
          </div>
        </div>

        {/* Informations générales */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-4">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-store-2-line text-primary-500"></i>
            Informations générales
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom de la boutique</label>
              <input
                type="text"
                value={form.nomcommerce}
                onChange={(e) => handleChange('nomcommerce', e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Description courte</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
              />
              <div className="text-xs text-foreground-400 mt-1">{(form.description || '').length}/500</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">À propos</label>
              <textarea
                value={form.aboutus}
                onChange={(e) => handleChange('aboutus', e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
              />
              <div className="text-xs text-foreground-400 mt-1">{(form.aboutus || '').length}/500</div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-4">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-phone-line text-accent-500"></i>
            Contact
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Téléphone</label>
              <input
                type="text"
                value={form.telephone}
                onChange={(e) => handleChange('telephone', e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Adresse</label>
              <input
                type="text"
                value={form.adresse}
                onChange={(e) => handleChange('adresse', e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Pays</label>
              <select
                value={form.pays}
                onChange={(e) => {
                  const nextPays = e.target.value;
                  setForm((prev) => ({ ...prev, pays: nextPays, ville: '' }));
                  setSaved(false);
                  setSaveError('');
                }}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
              >
                <option value="">— Sélectionner un pays —</option>
                {form.pays && !paysList.some((p) => p.nom_pays === form.pays) && (
                  <option value={form.pays}>{form.pays}</option>
                )}
                {paysList.map((p) => (
                  <option key={p.id} value={p.nom_pays}>{p.nom_pays}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Ville</label>
              <select
                value={form.ville}
                onChange={(e) => handleChange('ville', e.target.value)}
                disabled={!form.pays}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{form.pays ? '— Sélectionner une ville —' : 'Choisissez d\u2019abord un pays'}</option>
                {form.ville && !villeOptions.some((v) => v.nom_ville === form.ville) && (
                  <option value={form.ville}>{form.ville}</option>
                )}
                {villeOptions.map((v) => (
                  <option key={v.id} value={v.nom_ville}>{v.nom_ville}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Préférences */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 mb-4">
          <h3 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-settings-3-line text-secondary-700"></i>
            Préférences
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Devise</label>
              <div className="flex items-center gap-2 w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-700" title="Devise unique de votre compte">
                <i className="ri-lock-line text-foreground-400"></i>
                <span className="font-medium">{form.monaie || 'MAD'}</span>
              </div>
              <p className="text-xs text-foreground-400 mt-1">Modifiable une seule fois dans Paramètres&nbsp;→&nbsp;Profil. Elle s&apos;applique à toute votre boutique.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Langue</label>
              <select
                value={form.langue}
                onChange={(e) => handleChange('langue', e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors cursor-pointer"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Sauvegarde...
              </>
            ) : (
              <>
                <i className="ri-check-line"></i>
                Enregistrer
              </>
            )}
          </button>
          {saved && (
            <span className="text-sm text-accent-600 flex items-center gap-1">
              <i className="ri-checkbox-circle-line"></i>
              Sauvegardé !
            </span>
          )}
          {saveError && (
            <span className="text-sm text-red-600 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {saveError}
            </span>
          )}
        </div>
        </div>

        {/* ── Aperçu en direct du site public ── */}
        <div className="lg:sticky lg:top-20">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-eye-line text-accent-500"></i>
            <h3 className="text-sm font-semibold text-foreground-700">Aperçu de votre site public</h3>
          </div>
          <StorePreview
            name={form.nomcommerce}
            logo={form.image}
            banner={form.imageaboutus}
            color={form.couleurcharte}
            description={form.description}
            subdomain={subdomain}
            showProducts={caps.products}
            showServices={caps.services}
          />
          <p className="text-xs text-foreground-400 mt-2">
            L&apos;aperçu se met à jour en direct. Pensez à enregistrer vos modifications.
          </p>

          {/* Lien « MyLinks » à partager sur les réseaux sociaux */}
          <div className="mt-4 rounded-lg border border-background-200/70 bg-background-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-links-line"></i>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-900">Mon lien MyLinks</p>
                <p className="text-[11px] text-foreground-500">Un seul lien à partager sur vos réseaux sociaux</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={myLinksUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-background-100 border border-background-200/70 text-xs text-foreground-600 focus:outline-none"
              />
              <button
                type="button"
                onClick={copyMyLinks}
                disabled={!myLinksUrl}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
              >
                <i className={mylinksCopied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                {mylinksCopied ? 'Copié' : 'Copier'}
              </button>
            </div>
            {myLinksUrl && (
              <a
                href={myLinksUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-600 mt-2 no-underline"
              >
                Ouvrir ma page MyLinks
                <i className="ri-arrow-right-up-line"></i>
              </a>
            )}
          </div>

          {/* Personnalisation de la page MyLinks (fond, boutons libres, offre mise en avant) */}
          <MyLinksSettings user={user} />
        </div>
      </div>
    </div>
  );
}